<template>
  <mf-card-container title="Faturamento" class="faturamento-card">
    <mf-loading :boolean-loader="loading" loader-type="table">
      <v-container
        v-if="faturamentoTotalDoAno && faturamentoTotalDoAno.length"
        v-shortkey="['ctrl', 'alt', 'e']"
        fluid
        class="pa-0"
        grid-list-xs
        @shortkey="exportarCsv()"
      >
        <v-layout>
          <v-flex v-for="(faturamento, faturamentoIndex) in faturamentoTotalDoAno" :key="faturamentoIndex" xs4>
            <p class="mb-0 subheading ml-4 year-title">{{ faturamento.find((e) => e.dataMoment.isValid).dataMoment.toFormat('yyyy') }}</p>
            <v-data-table :items="faturamento" :headers="headers" hide-default-footer disable-pagination>
              <template v-slot:item="props">
                <tr :class="{ 'grey-background': props.index === lineIndex }" @mouseover="setLineIndex(props.index)" @mouseleave="removeLineIndex()">
                  <td v-if="faturamento.length !== props.index + 1">
                    <router-link v-if="props.item.data" class="link" :to="{ path: link(props.item.anoMes) }">{{
                      DateTime.fromISO(props.item.anoMes).setLocale('pt-BR').toFormat('MMMM') | capitalize
                    }}</router-link>
                    <span v-if="!props.item.data">-</span>
                  </td>
                  <td v-else class="bold">{{ props.item.data }}</td>

                  <td v-if="props.item.valor" class="text-right">{{ props.item.valor | localeString(2) | currency }}</td>
                  <td v-else class="text-right">-</td>

                  <td v-if="props.item.percentual" class="text-right">{{ props.item.percentual | localeString(1) | percent }}</td>
                  <td v-else class="text-right">-</td>

                  <td v-if="faturamentoTotalDoAno.length > 1">
                    <span v-if="lastIndex(faturamentoIndex, props.index, faturamento)">
                      <v-tooltip bottom class="text-left help-icon">
                        <template v-slot:activator="{ on }">
                          <span v-on="on">
                            <mf-icon small color="cinza-0">help</mf-icon>
                          </span>
                        </template>
                        <span
                          >Dados coletados até {{ DateTime.fromFormat(lastUpdate.last_updated.toString(), 'yyyyMMdd').toFormat('dd/MM/yyyy') }}.<br />
                          Faturamento do ano anterior até esta data: {{ faturamentoAnterior.valor | localeString(2) | currency }}</span
                        >
                      </v-tooltip>
                    </span>
                    <span v-else></span>
                  </td>
                </tr>
              </template>
            </v-data-table>
          </v-flex>
        </v-layout>
      </v-container>
      <v-container v-else class="my-6">
        <v-row align="center">
          <v-col align="center">
            <span class="subtitle">Nenhum resultado encontrado</span>
          </v-col>
        </v-row>
      </v-container>
    </mf-loading>
  </mf-card-container>
</template>

<script>
import { capitalize, floor, last, findLastIndex, groupBy } from 'lodash'
import { mapState } from 'vuex'
import { mapWaitingActions } from 'vue-wait'
import { agrupamentoMensal } from '@/config/helpers.js'
import { DateTime } from 'luxon'

export default {
  filters: {
    capitalize(value) {
      if (value) return capitalize(value)
      return ''
    },
  },
  props: {
    lista: [Array, Object],
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    faturamentoAnterior: {
      valor: null,
      percentual: null,
    },
    lineIndex: null,
    DateTime: DateTime,
  }),
  computed: {
    ...mapState({
      filters: (state) => state.filters,
      lastUpdate: (state) => state.lastUpdate,
    }),
    headers() {
      return [
        {
          text: 'Mês',
          sortable: false,
          width: '30%',
        },
        {
          text: 'Faturamento',
          sortable: false,
          width: '40%',
        },
        {
          text: '%',
          sortable: false,
          width: '30%',
        },
        {
          text: '',
          sortable: false,
          width: '10%',
        },
      ]
    },
    faturamentoMensal() {
      return agrupamentoMensal(this.lista, 'VAL_FATURAMENTO')
    },
    faturamentoPreenchido() {
      const faturamentoPreenchido = this.faturamentoMensal.reduce((acc, fatMes) => {
        // First month
        if (!acc.length) return [fatMes]
        let dt = last(acc).dataMoment.plus({ months: 1 })
        let df = fatMes.dataMoment

        while (dt < df) {
          acc.push({
            ano: dt.year,
            mes: dt.month,
            dataMoment: dt,
            data: capitalize(dt.toFormat('MMMM/yy')),
            anoMes: dt.toFormat('yyyy-MM'),
            percentual: 0,
            valor: 0,
          })
          dt = dt.plus({ months: 1 })
        }
        acc.push(fatMes)
        return acc
      }, [])

      const firstMonth = faturamentoPreenchido[0]
      const lastMonth = last(faturamentoPreenchido)

      if (firstMonth) {
        for (let mes = firstMonth.mes - 1; mes >= 1; mes--) {
          faturamentoPreenchido.unshift({
            ano: firstMonth.dataMoment.minus({ months: firstMonth.mes - mes }).year,
            dataMoment: firstMonth.dataMoment.minus({ months: firstMonth.mes - mes }),
            data: null,
            percentual: 0,
            valor: 0,
          })
        }
      }

      if (lastMonth) {
        for (let mes = lastMonth.mes + 1; mes <= 12; mes++) {
          faturamentoPreenchido.push({
            ano: lastMonth.dataMoment.toFormat('yyyy'),
            dataMoment: lastMonth.dataMoment.plus({ months: mes - lastMonth.mes }),
            data: null,
            percentual: 0,
            valor: 0,
          })
        }
      }

      return faturamentoPreenchido
    },
    faturamentoMensalComPorcentagem() {
      const lastFaturamento = last(this.faturamentoPreenchido.filter((fat) => fat.valor))
      return this.faturamentoPreenchido.map((faturamento, index) => {
        const faturamentoAnoAnterior = this.faturamentoMensal.find((fatAnt) => fatAnt.mes === faturamento.mes && fatAnt.ano === faturamento.ano - 1)

        if (faturamentoAnoAnterior) {
          faturamento.percentual = (faturamento.valor / faturamentoAnoAnterior.valor - 1) * 100
        }
        if (faturamento.anoMes === lastFaturamento.anoMes) faturamento.percentual = this.faturamentoAnterior.percentual
        return faturamento
      })
    },
    faturamentoMensalPorAno() {
      return Object.values(groupBy(this.faturamentoMensalComPorcentagem, 'ano'))
    },
    faturamentoTotalDoAno() {
      const faturamentosAnos = this.faturamentoMensalPorAno.map((ano, index, array) => {
        let faturamentoTotal = 0
        for (let mes of ano) {
          faturamentoTotal += mes.valor
        }
        if (index !== 0) {
          let percentualTotal = (faturamentoTotal / last(array[index - 1]).valor - 1) * 100
          ano.push({ ano: ano[0].ano, data: 'Total', valor: faturamentoTotal, percentual: percentualTotal })
        } else ano.push({ ano: ano[0].ano, data: 'Total', valor: faturamentoTotal })
        return ano
      })

      return faturamentosAnos.slice(-3)
    },
  },
  async mounted() {
    this.$nextTick(() => {
      this.$bus.$on('filtrar', this.getFaturamentoAnterior)
    })
  },
  destroyed() {
    this.$bus.$off('filtrar')
  },
  methods: {
    ...mapWaitingActions({ empresaFaturamentoDta: 'faturamento-anterior' }),
    link(param) {
      let date = DateTime.fromFormat(param, 'yyyy-MM').toFormat('yyyy-MM')
      return `/empresa/faturamento/diario/${date}`
    },
    getFaturamentoAnterior() {
      this.empresaFaturamentoDta({
        date: [DateTime.fromFormat(this.lastUpdate.last_updated.toString(), 'yyyyMMdd').toFormat('yyyy-MM')],
        unidades: this.filters.unitsConfiguration,
        categoria_cliente: this.filters.cliente,
        tipo_pessoa: this.filters.pessoa,
        fidelidade: this.filters.fidelidade,
        segmentos: this.filters.segmentos,
      }).then((data) => {
        this.faturamentoAnterior.valor = data['VAL_FATURAMENTO_DTA']
        this.faturamentoAnterior.percentual = data['PER_FATURAMENTO_DTA']
      })
    },
    setLineIndex(index) {
      this.lineIndex = index
    },
    removeLineIndex() {
      this.lineIndex = null
    },
    lastIndex(arrayIndex, propertyIndex, faturamento) {
      const index = findLastIndex(faturamento.slice(0, -1), (object) => object.valor)
      const lastArray = Boolean(arrayIndex === this.faturamentoTotalDoAno.length - 1)
      const lastObject = Boolean(propertyIndex === index)
      return Boolean(lastArray && lastObject)
    },
    exportarCsv() {
      let array = []

      for (let indexAno in this.faturamentoMensalPorAno) {
        for (let indexMes = 0; indexMes < this.faturamentoMensalPorAno[indexAno].length - 1; indexMes++) {
          let objeto = this.faturamentoMensalPorAno[indexAno][indexMes]
          array.push([objeto.data, floor(objeto.valor, 2)])
        }
      }
      let csvContent = 'data:text/csv;charset=utf-8,'
      array.forEach(function (rowArray) {
        let row = rowArray.join(';')
        csvContent += row + '\r\n'
      })

      let encodedUri = encodeURI(csvContent)
      let link = document.createElement('a')
      link.setAttribute('href', encodedUri)
      link.setAttribute('download', 'my_data.csv')
      link.innerHTML = 'Click Here to download'
      document.body.appendChild(link) // Required for FF

      link.click()
    },
  },
}
</script>
<style lang="scss">
.faturamento-card {
  min-height: 250px;
  .bold {
    font-weight: bold;
  }
  .grey-background {
    background-color: $cinza-l90;
    transition: background-color 0.2s cubic-bezier(0.25, 0.8, 0.5, 1);
  }
  .year-title {
    color: rgba(0, 0, 0, 0.54);
  }
}
</style>
